/**
 * QuotaSuspensionAlert — alerta de quota excedida (Card no Gestor)
 *
 * Consome GET /api/manager/alerts/quota (gestor via JWT) — backend filtra por
 * institution_id resolvido do role 'GESTOR' no club ativo (cross-tenant blindado).
 *
 * Response shape do backend:
 *   {
 *     success: true,
 *     data: [
 *       {
 *         alert_id, alert_type: 'plan_quota_exceeded', severity: 'MEDIUM',
 *         message, triggered_at,
 *         plan_key, athletes_suspended_count,
 *         suspended_athletes: [{athlete_id, full_name, ...}]
 *       },
 *       ...
 *     ]
 *   }
 *
 * Comportamento:
 *   - 0 alerts ou nenhum atleta suspenso  →  retorna null (componente não aparece)
 *   - 1+ alerts com atletas suspensos     →  renderiza card vermelho dessaturado
 *
 * Quando há múltiplos alerts (raro mas possível), unificamos atletas suspensos
 * (dedup por athlete_id) e exibimos o plan_key do alerta MAIS RECENTE.
 *
 * UI_PADRAO: paleta cinza por padrão; vermelho dessaturado
 *            (#fef2f2 bg, #7f1d1d text, #f3d1d1 border, #a93333 dot)
 *            é o ÚNICO uso de cor — sinal de limite/clínico legítimo.
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { AZURE } from '../../services/api';
import { useSportsLang } from '../../contexts/SportsLangContext';

const ALERT = {
  bg:     '#fef2f2',
  text:   '#7f1d1d',
  border: '#f3d1d1',
  dot:    '#a93333',
};

const LABELS = {
  pt: {
    title: (n) => `${n} ${n === 1 ? 'atleta suspenso' : 'atletas suspensos'} por limite do plano`,
    subtitle: (planKey) =>
      `Seu plano atual (${planKey}) atingiu o limite de atletas. Os nomes abaixo estão temporariamente sem acesso operacional.`,
    upgradeNote: 'Para reativar, faça upgrade do plano ou remova atletas excedentes. Fale com o time comercial.',
    nameMore: (n) => `… +${n} mais`,
  },
  en: {
    title: (n) => `${n} athlete${n === 1 ? '' : 's'} suspended by plan quota`,
    subtitle: (planKey) =>
      `Your current plan (${planKey}) hit the athletes limit. The names below are temporarily without operational access.`,
    upgradeNote: 'To reactivate, upgrade the plan or remove exceeding athletes. Contact the commercial team.',
    nameMore: (n) => `… +${n} more`,
  },
};

const QuotaSuspensionAlert = () => {
  const { lang } = useSportsLang();
  const T        = LABELS[lang] || LABELS.pt;

  const [alerts,  setAlerts]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(false);

  useEffect(() => {
    let mounted = true;
    AZURE.get('/manager/alerts/quota')
      .then((resp) => {
        if (!mounted) return;
        // Backend retorna {success, data:[...alerts...], meta:{}}
        const list = resp.data?.data || [];
        setAlerts(Array.isArray(list) ? list : []);
      })
      .catch(() => { if (mounted) setErr(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Não renderiza nada se: carregando, erro, ou nenhum alerta.
  if (loading || err) return null;
  if (!alerts.length) return null;

  // Unifica atletas suspensos de todos os alerts (dedup por athlete_id).
  // Backend já ordena alerts DESC por triggered_at, então pegamos plan_key do
  // alerta mais recente como referência principal.
  const seen        = new Set();
  const allSuspended = [];
  alerts.forEach((a) => {
    (a.suspended_athletes || []).forEach((ath) => {
      if (ath.athlete_id != null && !seen.has(ath.athlete_id)) {
        seen.add(ath.athlete_id);
        allSuspended.push(ath);
      }
    });
  });

  const suspendedCount = allSuspended.length;
  if (suspendedCount === 0) return null;

  const planKey  = alerts[0]?.plan_key || '—';
  const visible  = allSuspended.slice(0, 10);
  const overflow = Math.max(0, suspendedCount - visible.length);

  return (
    <div
      className="rounded-2xl border p-5 mb-6"
      style={{ backgroundColor: ALERT.bg, borderColor: ALERT.border }}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#fff', border: `1px solid ${ALERT.border}` }}
        >
          <AlertTriangle size={18} style={{ color: ALERT.text }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold leading-tight" style={{ color: ALERT.text }}>
            {T.title(suspendedCount)}
          </h3>
          <p className="text-[12px] mt-1 leading-relaxed" style={{ color: ALERT.text, opacity: 0.85 }}>
            {T.subtitle(planKey)}
          </p>

          <ul className="text-[12px] mt-3 space-y-0.5 pl-1" style={{ color: ALERT.text }}>
            {visible.map((a) => (
              <li key={a.athlete_id} className="flex items-center gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: ALERT.dot }}
                />
                <span className="font-medium">{a.full_name || `Atleta #${a.athlete_id}`}</span>
              </li>
            ))}
            {overflow > 0 && (
              <li className="italic opacity-70 pl-3.5">{T.nameMore(overflow)}</li>
            )}
          </ul>

          <p className="text-[11px] mt-3 italic" style={{ color: ALERT.text, opacity: 0.75 }}>
            {T.upgradeNote}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuotaSuspensionAlert;
